import { DateTime } from 'luxon'

import { StoppageCalc } from '../DTOs/Stoppage/StoppageCalc'
import { StoppageDto } from '../DTOs/Stoppage/StoppageDto'
import { queryClient } from '../queryClient'

type CachedPage = { data?: unknown[] }

const NON_MAINTENANCE_STOPPAGE_TYPE_IDS = new Set([1, 3, 4])

export interface OperationStatusPatch {
  status: string
  statusDescription: string
  stoppageColourHub?: string
  stoppageId?: number
  currentStoppageTypeId?: number
  currentStoppageSubTypeId?: number
  timeIn?: DateTime
}

export function mirrorPlantStoppagesForStatus(
  plantId: number,
  dateIso: string,
  shift: string,
  status: string | undefined,
): void {
  if (!plantId || !status) return

  for (const [key, cached] of queryClient.getQueriesData<CachedPage>({
    queryKey: ['dataTable', 'operations', 'plantStoppages', dateIso, shift],
  })) {
    const parts = key as unknown[]
    if (parts[5] !== plantId || parts[6] === status || !Array.isArray(cached?.data)) continue

    const target = [...parts]
    target[6] = status
    queryClient.setQueryData(target, cached)
  }
}

export function appendCachedPlantStoppage(dto: StoppageDto, dateIso: string, shift: string, plantId: number): void {
  if (!plantId) return

  for (const [key, cached] of queryClient.getQueriesData<CachedPage>({
    queryKey: ['dataTable', 'operations', 'plantStoppages', dateIso, shift],
  })) {
    const rows = cached?.data
    if ((key as unknown[])[5] !== plantId || !Array.isArray(rows)) continue
    if (dto.stoppageId && rows.some(row => isSameStoppage(row, dto.stoppageId!))) continue

    queryClient.setQueryData(key, {
      ...cached,
      data: [...rows, dto],
      totalCount: ((cached as { totalCount?: number })?.totalCount ?? rows.length) + 1,
    })
  }
}

export function patchCachedOperationAvailability(
  plantId: number,
  dateIso: string,
  shift: string,
  closedStoppageId?: number,
): void {
  if (!plantId) return

  for (const [, cached] of queryClient.getQueriesData<CachedPage>({
    queryKey: ['dataTable', 'operations', 'plantStoppages', dateIso, shift],
  })) {
    const rows = cached?.data
    if (!Array.isArray(rows)) continue
    for (const row of rows as StoppageDto[]) {
      if (row?.plantId !== plantId || row.endTime != null) continue
      if (closedStoppageId && row.stoppageId === closedStoppageId) continue
      return
    }
  }

  patchCachedOperationStatus(plantId, dateIso, shift, {
    status: 'UP',
    statusDescription: '',
    stoppageColourHub: '',
    stoppageId: undefined,
    currentStoppageTypeId: undefined,
    currentStoppageSubTypeId: undefined,
    timeIn: undefined,
  })
}

export function patchCachedOperationStatus(
  plantId: number,
  dateIso: string,
  shift: string,
  patch: OperationStatusPatch,
): void {
  if (!plantId) return

  mirrorPlantStoppagesForStatus(plantId, dateIso, shift, patch.status)

  for (const [key, cached] of queryClient.getQueriesData<CachedPage>({
    queryKey: ['dataTable', 'operations', 'stoppageCalcs', dateIso, shift],
  })) {
    const rows = cached?.data
    if (!Array.isArray(rows) || !rows.some(row => (row as StoppageCalc)?.plantId === plantId)) continue

    queryClient.setQueryData(key, {
      ...cached,
      data: rows.map(row => (row as StoppageCalc)?.plantId === plantId
        ? new StoppageCalc({ ...(row as StoppageCalc), ...patch })
        : row),
    })
  }
}

export function appendCachedStoppage(dto: StoppageDto, dateIso: string, shift: string): void {
  if (NON_MAINTENANCE_STOPPAGE_TYPE_IDS.has(dto.stoppageTypeId)) return

  const withNavObjects = new StoppageDto({ ...dto, plant: dto.plant ?? cachedPlantFor(dto.plantId) })

  for (const [key, cached] of queryClient.getQueriesData<CachedPage>({
    queryKey: ['dataTable', 'stoppages', 'list', dateIso, shift],
  })) {
    const rows = cached?.data
    if (!Array.isArray(rows)) continue

    const existing = withNavObjects.stoppageId
      ? rows.findIndex(row => isSameStoppage(row, withNavObjects.stoppageId!))
      : -1

    if (existing >= 0) {
      const known = rows[existing] as StoppageDto
      if (known.plant || !withNavObjects.plant) continue

      queryClient.setQueryData(key, {
        ...cached,
        data: rows.map((row, index) => (index === existing ? withNavObjects : row)),
      })
      continue
    }

    queryClient.setQueryData(key, {
      ...cached,
      data: [withNavObjects, ...rows],
      totalCount: ((cached as { totalCount?: number })?.totalCount ?? rows.length) + 1,
    })
  }
}

export function cachedPlantFor(plantId: number | undefined): StoppageDto['plant'] {
  if (!plantId) return undefined

  for (const [, cached] of queryClient.getQueriesData<CachedPage>({ queryKey: ['dataTable'] })) {
    const rows = cached?.data
    if (!Array.isArray(rows)) continue

    for (const row of rows as StoppageDto[]) {
      if (row?.plantId === plantId && row.plant?.department && 'startTime' in row) return row.plant
    }
  }
  return plantFromAnyCache(plantId)
}

function plantFromAnyCache(plantId: number): StoppageDto['plant'] {
  for (const [, cached] of queryClient.getQueriesData({ queryKey: [] })) {
    const rows = Array.isArray(cached) ? cached : (cached as CachedPage)?.data
    if (!Array.isArray(rows)) continue

    for (const row of rows) {
      const candidate = row as { id?: number; plantId?: number; plantRef?: string; department?: unknown }
      if (!candidate?.plantRef || !candidate.department) continue
      if (candidate.id === plantId || candidate.plantId === plantId) return candidate as StoppageDto['plant']
    }
  }

  for (const [, cached] of queryClient.getQueriesData<CachedPage>({
    queryKey: ['dataTable', 'operations', 'stoppageCalcs'],
  })) {
    for (const row of (cached?.data ?? []) as StoppageCalc[]) {
      if (row?.plantId === plantId && row.machineName) {
        return { id: plantId, plantRef: row.machineName } as StoppageDto['plant']
      }
    }
  }
  return undefined
}

function isSameStoppage(row: unknown, stoppageId: number): boolean {
  if (!row || typeof row !== 'object' || !('startTime' in row)) return false
  return (row as StoppageDto).stoppageId === stoppageId
}

export function patchCachedStoppages(updated: StoppageDto): StoppageDto | undefined {
  const stoppageId = updated.stoppageId
  if (!stoppageId) return undefined

  const navProps = new Set(['plant', 'stoppageType', 'stoppageSubType', 'location'])
  const changed = Object.fromEntries(
    Object.entries(updated).filter(([field, value]) =>
      value !== undefined && !(value === null && navProps.has(field)))
  )

  let merged: StoppageDto | undefined

  for (const [key, cached] of queryClient.getQueriesData<CachedPage>({ queryKey: ['dataTable'] })) {
    const rows = cached?.data
    if (!Array.isArray(rows) || !rows.some(row => isSameStoppage(row, stoppageId))) continue

    queryClient.setQueryData(key, {
      ...cached,
      data: rows.map(row => {
        if (!isSameStoppage(row, stoppageId)) return row
        merged = new StoppageDto({ ...(row as StoppageDto), ...changed })
        return merged
      }),
    })
  }

  return merged
}
